/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Oct 30 2023
 **/

import { uniqBy } from "lodash-es";

import {
	Avatar,
	Badge,
	Icon,
	Row,
	useModal,
	TypographyTooltip,
	Button,
	AccordionTemplate
} from "@/frameworks/designStuff/components";
import { EventID, GROUP_POPOVER_ID, ModalID, SidebarID, ZIndex } from "@/frameworks/core/commons";
import {
	hasNewMessageInSpaceChat,
	hasPinMessageInSpaceChat,
	hasDraftMessageInSpaceChat,
	useChatMessageActions,
	useChatRoomActions,
	useChatRoomListActions
} from "@/frameworks/core/chat";
import { useChatListFocusScroll } from "@/frameworks/core/components/Chat/private";
import { InviteChatMember, ShowChatMember, PanelRoomItem } from "@/frameworks/core/components";
import { usePopover, useFullSizeChatManager, useSidebarActions } from "@/frameworks/core/hooks";
import { Maybe } from "@/frameworks/libraries/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { Configure } from "@/frameworks/libraries/utilities";
import { colors } from "@/frameworks/core/styles";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useChatDataStore } from "@/modules/chat/store";
import { _T } from "@rfice/language";
import { EventHandler } from "@rfice/eventhandler";
import { SpaceChannelOption } from "../../ChatView/FullSizeChat/space/components";
import * as S from "../emotion";

type SpaceDirectMessageProps = {
	spaceData: WebServices.SpaceChat.List.Data[number];
	currentChatSelected: string;
	handleSelectChat: (channel: string) => void;
};

const PADDING_OFFSET = 32;
const ELLIPSIS_OFFSET = 6;

export default function SpaceDirectMessage({
	spaceData,
	handleSelectChat,
	currentChatSelected
}: SpaceDirectMessageProps) {
	const properties = useProperties();
	const chatRoomListActions = useChatRoomListActions();
	const sidebarActions = useSidebarActions();
	const modal = useModal();
	const chatRoomActions = useChatRoomActions();
	const popover = usePopover();
	const { fullSizeChatActions } = useFullSizeChatManager();
	const { messageReaded } = useChatMessageActions();
	const showFullSizeChat = useChatDataStore((state) => state.show_fullsize_chat);
	const { chats } = usePersistActions();

	const spaceRoomList = uniqBy(spaceData.rooms, "roomId").filter(
		(room) => (room.private && room.roomJoined) || (!room.private && !room.deleted)
	);
	const isExposedSpaceRoomList = spaceRoomList.length > 1;

	const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
		evt.stopPropagation();
		const {
			sidebar,
			chatkind,
			modal: _modal,
			channel = "",
			space = "",
			newMessage,
			messageId = ""
		} = evt.currentTarget.dataset;
		if (channel) {
			if (showFullSizeChat) {
				if (fullSizeChatActions.get()) {
					EventHandler.emit(EventID.CHANGE_FULL_SIZE_CHAT_TAB, channel);
				}
			}
			chatRoomActions.common.open({ channelID: channel, spaceID: space, type: "Space" });
			handleSelectChat(channel);
			if (newMessage) messageReaded({ channelID: channel, messageID: messageId });
		}

		if (_modal) {
			switch (_modal) {
				case "invite-chat-member":
					{
						modal.show({
							id: ModalID.INVITE_CHAT_MEMBER,
							content: (
								<InviteChatMember
									chatKind={chatkind as ChatServices.RoomType}
									onHide={() => {
										modal.hide(ModalID.INVITE_CHAT_MEMBER);
									}}
								/>
							)
						});
					}
					break;

				case "show-chat-member": {
					modal.show({
						id: ModalID.SHOW_CHAT_MEMBER,
						content: (
							<ShowChatMember
								spaceID={spaceData.spaceId}
								onHide={() => modal.hide(ModalID.SHOW_CHAT_MEMBER)}
								channelID={channel}
								chatKind={chatkind as ChatServices.RoomType}
							/>
						)
					});
				}
			}
		}

		if (sidebar) {
			switch (sidebar) {
				default: {
					sidebarActions.show(sidebar as SidebarID);
					break;
				}
			}
		}
	};

	const handleMoreClick = async (evt: React.MouseEvent<HTMLButtonElement>) => {
		const { channel = "", space = "" } = evt.currentTarget.dataset;
		const { currentTarget } = evt;
		evt.stopPropagation();
		const spaceInfo = chatRoomListActions.getSpaceChannel(space);
		const room = (spaceInfo as WebServices.SpaceChat.List.Data[number]).rooms.find((e) => e.roomId === channel);
		if (!room) return;
		const { adminCount, isCurrentUserAdmin } = room.users.reduce(
			(acc, user) => {
				if (user.spaceAuthority === "SPACE_ADMIN") {
					acc.adminCount++;
					if (user.id === Configure.userID) {
						acc.isCurrentUserAdmin = true;
					}
				}
				return acc;
			},
			{ adminCount: 0, isCurrentUserAdmin: false }
		);
		popover.show({
			popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
			uniqueID: "space_option",
			content: (
				<SpaceChannelOption
					spaceID={space}
					isOnlyAdmin={adminCount === 1 && isCurrentUserAdmin}
					notificationType={room.notificationType}
					roomID={room.roomId}
					roomType={room.general ? "general" : room.private ? "private" : "public"}
					users={room.users}
				/>
			),
			anchorEl: currentTarget,
			direction: {
				x: "right",
				y: "bottom"
			},
			position: {
				x: 0,
				y: -40
			},
			zIndex: ZIndex.POPUP
		});
	};
	const hasNewMessage = hasNewMessageInSpaceChat(spaceData);
	const hasPinMessage = hasPinMessageInSpaceChat(spaceData);
	const hasDraftMessage = hasDraftMessageInSpaceChat(spaceData);

	const handleShowGeneralChat = (channel: string, space: string) => {
		if (showFullSizeChat) {
			if (fullSizeChatActions.get()) {
				EventHandler.emit(EventID.CHANGE_FULL_SIZE_CHAT_TAB, channel);
			}
		}
		chatRoomActions.common.open({ channelID: channel, spaceID: space, type: "Space" });
	};

	const handleIdAccordion = (e: React.MouseEvent<HTMLElement>) => {
		const target = e.currentTarget as HTMLElement;
		const { isOpen } = target.dataset;
		const channel = spaceData.rooms[0].roomId;
		const space = spaceData.rooms[0].spaceId ?? "";
		const messageId = spaceData.rooms.find(() => true)?.lastSentMessage;
		const isOpenRoom = isOpen !== "true";
		const isOpenMyRoomAndHasSpaceName = spaceData.myRoom && spaceData.spaceId;
		const isOpenMyRoomAndNoSpaceName = spaceData.myRoom && isOpenRoom;
		const isOpenRoomNotMyRoom = spaceData.rooms.length === 1 && isOpenRoom && !spaceData.myRoom;
		if (isOpenMyRoomAndHasSpaceName) {
			if (isOpenRoom) {
				handleShowGeneralChat(channel, space);
			}
		} else {
			if (isOpenRoomNotMyRoom || isOpenMyRoomAndNoSpaceName) {
				handleShowGeneralChat(channel, space);
			}
		}

		// return 이유는 isExposedSpaceRoomList 가 true일때는 단순 아코디언 기능만 동작하기 떄문에 뒤의 messageReaded 실행 하면안됨.
		if (isExposedSpaceRoomList) return;
		else handleShowGeneralChat(channel, space);

		if (hasNewMessage) messageReaded({ channelID: channel, messageID: String(messageId) });
	};

	useChatListFocusScroll(spaceData?.rooms?.[0]?.roomId ?? "");

	return (
		<AccordionTemplate
			container={{
				activeId: spaceData.spaceId
			}}
			accordion={{
				id: spaceData.spaceId
			}}
			header={{
				option: {
					onClick: handleIdAccordion
				},
				render: ({ isAccordionOpen }) => (
					<Row
						verticalAlign="center"
						css={S.listItemCss()}
						id={"panelRoom-" + `${spaceData.rooms[0]?.roomId}`}
					>
						<S.HoverElm
							isExposedSpaceRoomList={isExposedSpaceRoomList}
							activeBackground={
								spaceData.rooms.length > 1 ? false : currentChatSelected === spaceData.rooms[0]?.roomId
							}
						>
							<Maybe test={isExposedSpaceRoomList}>
								<Row>
									<Maybe test={isAccordionOpen} fallback={<Icon name="RightFilled16" size={16} />}>
										<Icon name="DownFilled16" size={16} />
									</Maybe>
								</Row>
							</Maybe>
							<div
								css={{
									width: "32px",
									height: "32px"
								}}
							>
								<Avatar
									type="image"
									kind="space_32"
									text={spaceData.spaceName || ""}
									src={`${properties.default.webDomain}${spaceData.thumbnailPath}`}
								/>
							</div>
							<Row
								horizonAlign="distribute"
								verticalAlign="center"
								css={{
									width: `calc(100% - ${PADDING_OFFSET}px - ${ELLIPSIS_OFFSET}px)`
								}}
								gap={7}
							>
								<TypographyTooltip.p
									tooltipProps={{ hasIcon: false }}
									componentProps={{
										maxLine: 1,
										stretch: true,
										ellipsis: true,
										style: { marginLeft: "4px", flex: 1 },
										typoSize: hasNewMessage ? "Title3_B" : "Title3"
									}}
								>
									{spaceData.myRoom
										? spaceData.spaceName !== undefined && spaceData.spaceName.length > 0
											? spaceData.spaceName
											: _T("마이룸")
										: spaceData.spaceName}
								</TypographyTooltip.p>
								<Maybe test={isExposedSpaceRoomList}>
									<Row
										style={{ minWidth: "16px" }}
										gap={8}
										className="notify-icons"
										verticalAlign="center"
										horizonAlign="center"
									>
										<Maybe test={hasDraftMessage}>
											<Icon name="Edit24" size={16} fill={colors.neutral_50_100} />
										</Maybe>
										<Maybe test={hasPinMessage}>
											<Icon name="Pin16" size={16} fill={colors.neutral_50_100} />
										</Maybe>
										<Maybe test={hasNewMessage}>
											<Badge type="mediumDot" />
										</Maybe>
									</Row>
									<Maybe.Fallback>
										<Maybe test={hasDraftMessage}>
											<Icon
												className="notify-icon"
												name="Edit24"
												size={16}
												fill={colors.neutral_50_100}
											/>
										</Maybe>
										<Maybe test={hasPinMessage}>
											<Icon
												className="notify-icon"
												name="Pin16"
												size={16}
												fill={colors.neutral_50_100}
											/>
										</Maybe>
										<Button.Icon
											name={"More24"}
											size="medium"
											onClick={handleMoreClick}
											data-space={spaceData.spaceId}
											data-channel={spaceData.rooms.find(() => true)?.roomId}
											className="more-icon"
										/>
										<Maybe test={hasNewMessage}>
											<Badge type="mediumDot" />
										</Maybe>
									</Maybe.Fallback>
								</Maybe>
							</Row>
						</S.HoverElm>
					</Row>
				)
			}}
			body={{
				option: {
					style: { width: "100%" }
				},
				render: () => (
					<Maybe test={isExposedSpaceRoomList}>
						<S.SpaceGroup css={{ width: "392px" }}>
							{uniqBy(spaceData.rooms, "roomId")
								.filter((room) => (room.private && room.roomJoined) || (!room.private && !room.deleted))
								.map((room) => {
									const _badgeList = [] as ("new" | "pin")[];
									if (room.hasNewMessage) _badgeList.push("new");
									if (room.pinned) _badgeList.push("pin");
									if (room.hide) return;
									const hasDraft = chats.has_draft(room.roomId);
									return (
										<S.StyledRoomItem key={room.roomId}>
											<PanelRoomItem
												id={"panelRoom-" + `${room.roomId}`}
												isVoiceCall={room.inVoiceChat}
												hideUserIcon={true}
												roomID={room.roomId}
												onClick={handleClick}
												onMoreClick={handleMoreClick}
												data-new-message={room.hasNewMessage}
												data-message-id={room.lastSentMessage}
												data-sidebar={showFullSizeChat ? SidebarID.FullSizeChat : ""}
												title={room.roomName!}
												private={room.private}
												typoSize={"Body3_MD"}
												badgeList={_badgeList}
												hasDraft={hasDraft}
												data-space={room.spaceId}
												data-chatkind="Space"
												data-channel={room.roomId}
												data-name={room.roomName}
												activeBackground={
													currentChatSelected === room.roomId ||
													currentChatSelected === room.spaceId
												}
											/>
										</S.StyledRoomItem>
									);
								})}
						</S.SpaceGroup>
					</Maybe>
				)
			}}
		/>
	);
}
